import React, { useEffect, useState } from 'react';

import claimsIcon from '@/assets/claimsIcon.png';
import corporateUserIcon from '@/assets/corporateUserIcon.png';
import golfCourseIcon from '@/assets/golfCourseIcon.png';
import homeIcon from '@/assets/homeIcon.png';
import inviteIcon from '@/assets/inviteIcon.png';
import passwordIcon from '@/assets/passwordIcon.png';
import purchaseIcon from '@/assets/purchaseIcon.png';
import rewardTierIcon from '@/assets/rewardTierIcon.png';
import signOutIcon from '@/assets/signOutIcon.png';
import voucherIcon from '@/assets/voucherIcon.png';
import WhiteLogo from '@/components/controls/images/navbar/WhiteLogo.tsx';
import NavItem from '@/components/controls/NavItem.tsx';
import { useAuthStore } from '@/stores/useAuthStore.ts';

const navItems = [
  {
    icon: homeIcon,
    label: 'Dashboard',
    url: '/',
    section: 'Home',
    requireAuth: false,
    roles: ['admin', 'corporate'],
  },
  {
    icon: golfCourseIcon,
    label: 'Golf Courses',
    url: '/golf-course',
    section: 'Manage',
    requireAuth: true,
    roles: ['admin'],
  },
  {
    icon: rewardTierIcon,
    label: 'Reward Tiers',
    url: '/reward-tier',
    section: 'Manage',
    requireAuth: true,
    roles: ['admin'],
  },
  {
    icon: claimsIcon,
    label: 'Claims',
    url: '/claims-list',
    section: 'Manage',
    requireAuth: true,
    roles: ['admin'],
  },
  {
    icon: voucherIcon,
    label: 'Vouchers',
    url: '/vouchers',
    section: 'Manage',
    requireAuth: true,
    roles: ['admin', 'corporate'],
  },
  {
    icon: corporateUserIcon,
    label: 'Corporate Users',
    url: '/corporate-users',
    section: 'Manage',
    requireAuth: true,
    roles: ['admin'],
  },
  {
    icon: purchaseIcon,
    label: 'Purchase Vouchers',
    url: '/purchase-vouchers',
    section: 'Manage',
    requireAuth: true,
    roles: ['corporate'],
  },
  {
    icon: inviteIcon,
    label: 'Invite corporate user',
    url: '/invite-corporate-user',
    section: 'Authentication',
    requireAuth: true,
    roles: ['admin'],
  },
  {
    icon: passwordIcon,
    label: 'Change Password',
    url: '/change-password',
    section: 'Account',
    requireAuth: true,
    roles: ['admin', 'corporate'],
  },
  {
    icon: 'pi pi-lock',
    label: 'Sign In',
    url: '/sign-in',
    section: 'Account',
    requireAuth: false,
    roles: [],
  },
];

const Sidebar: React.FC = () => {
  const { isLoggedIn, role } = useAuthStore();
  const [navItemsToShow, setNavItemsToShow] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setNavItemsToShow(
      navItems.filter((item) => {
        if (item.url === '/sign-in') {
          return true;
        }

        if (item.requireAuth && !isLoggedIn) {
          return false;
        }

        if (item.roles.length && !item.roles.includes(role)) {
          return false;
        }

        return true;
      })
    );
  }, [isLoggedIn, role]);

  const groupedNavItems = navItemsToShow.reduce(
    (acc, item) => {
      if (!acc[item.section]) {
        // eslint-disable-next-line no-param-reassign
        acc[item.section] = [];
      }
      acc[item.section].push(item);
      return acc;
    },
    {} as { [key: string]: typeof navItems }
  );

  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = (): void => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="fixed top-4 left-4 z-[110] xl:hidden bg-primary-300 text-white p-3 rounded-lg shadow-lg hover:bg-primary-200 transition-colors"
        aria-label="Toggle menu"
      >
        <i
          className={`pi ${isMobileMenuOpen ? 'pi-times' : 'pi-bars'} text-xl`}
        />
      </button>

      {isMobileMenuOpen ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] xl:hidden"
          onClick={closeMobileMenu}
        />
      ) : null}

      <aside
        className={`flex flex-col items-start pt-8 pb-0 h-screen fixed left-0 top-0 overflow-hidden transition-all duration-300 ease-in-out
          ${isMobileMenuOpen ? 'z-[100]' : 'z-[5]'}
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          w-64
          xl:translate-x-0 xl:w-[16%] 2xl:w-[14.5%]
        `}
        style={{
          backgroundImage: 'url(/SidebarBackground.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-sidebar-green-gradient" />

        <div className="relative z-10 flex flex-col h-full w-full">
          <div className="flex justify-center mb-6 px-4">
            <div className="flex items-center justify-center h-32 w-32 md:h-40 md:w-40 xl:h-48 xl:w-48">
              <WhiteLogo />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 hide-scrollbar">
            {Object.keys(groupedNavItems).map((section) => (
              <div key={section} className="mb-6">
                <h3 className="text-xs font-bold uppercase text-white/40 mb-2 px-3 1.5xl:px-6 2xl:px-8">
                  {section}
                </h3>
                {groupedNavItems[section].map((item) =>
                  isLoggedIn && item.url === '/sign-in' ? (
                    <div onClick={closeMobileMenu} key="Sign out icon">
                      <NavItem
                        icon={signOutIcon}
                        label="Sign Out"
                        url="/sign-out"
                      />
                    </div>
                  ) : (
                    <div onClick={closeMobileMenu} key={item.label}>
                      <NavItem
                        icon={item.icon}
                        label={item.label}
                        url={item.url}
                      />
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
